import {
  columnarDecode,
  columnarEncode,
  keyChars,
  type TranspositionGrid,
} from "@/lib/tool-kit/ciphers/columnar";
import type { CipherDirection } from "@/lib/tool-kit/ciphers-polygraphic/keyed-letters";
import {
  buildSquare,
  type GridAlphabet,
  gridSize,
  gridText,
  type SquareFill,
  squarePoint,
} from "@/lib/tool-kit/ciphers-polygraphic/squares";

/**
 * The ADFGX and ADFGVX ciphers of the German army, 1918.
 *
 * Two stages. First a Polybius square — 5 × 5 for ADFGX, 6 × 6 for ADFGVX, with the rows
 * and columns labelled by those five or six letters — turns every plain character into
 * two letters, row first (**fractionation**). Then the whole run of letters is written in
 * rows under a transposition keyword and the columns are read off in the alphabetical order
 * of the keyword's letters (a columnar **transposition**). The last row is left short, not
 * padded, and equal keyword letters are taken left to right.
 *
 * ADFGX uses 25 cells (I and J sharing one, or Q left out); ADFGVX uses 36 and holds the
 * letters and the digits 0–9. Both squares were mixed alphabets that changed daily.
 */

const LABELS_5 = "ADFGX";
const LETTER_OR_DIGIT = /\p{L}|\p{N}/u;
const LABELS_6 = "ADFGVX";

export interface AdfgvxOptions {
  /** A keyword for the square — or all 25 or 36 characters, to type the square out. */
  keyword: string;
  kind: GridAlphabet;
  fill: SquareFill;
  /** The transposition keyword. */
  transposition: string;
}

/** The letters that label the rows and columns: ADFGX or ADFGVX. */
export function adfgvxLabels(kind: GridAlphabet): string {
  return kind === "alnum" ? LABELS_6 : LABELS_5;
}

export interface AdfgvxPair {
  /** The plain character. */
  plain: string;
  /** Its two letters: row label, column label. */
  pair: string;
}

export interface AdfgvxResult {
  square: string;
  size: number;
  labels: string;
  /** Every character with its pair of letters, in message order. */
  pairs: AdfgvxPair[];
  /** The run of pair letters before (encoding) or after (decoding) the transposition. */
  fractionated: string;
  /** The transposition grid; null when the keyword is too short to use. */
  grid: TranspositionGrid | null;
  output: string;
  droppedQ: number;
  /** Decoding: characters that are not row or column labels. */
  invalid: number;
  /** Decoding: a last letter with no partner. */
  unpaired: string;
}

/** A transposition keyword needs at least two usable characters to move anything. */
export function transpositionUsable(keyword: string): boolean {
  return keyChars(keyword).length >= 2;
}

function emptyResult(
  square: string,
  size: number,
  labels: string
): AdfgvxResult {
  return {
    square,
    size,
    labels,
    pairs: [],
    fractionated: "",
    grid: null,
    output: "",
    droppedQ: 0,
    invalid: 0,
    unpaired: "",
  };
}

function encode(
  text: string,
  options: AdfgvxOptions,
  base: AdfgvxResult
): AdfgvxResult {
  const { chars, droppedQ } = gridText(text, options.kind);
  const pairs: AdfgvxPair[] = [];
  for (const plain of chars) {
    const point = squarePoint(base.square, base.size, plain);
    pairs.push({
      plain,
      pair: `${base.labels.charAt(point.row)}${base.labels.charAt(point.column)}`,
    });
  }
  const fractionated = pairs.map((item) => item.pair).join("");
  const moved = columnarEncode(fractionated, {
    key: options.transposition,
    secondKey: null,
    filler: "",
  });
  return {
    ...base,
    pairs,
    fractionated,
    grid: moved.grids[0] ?? null,
    output: moved.output,
    droppedQ,
  };
}

function decode(
  text: string,
  options: AdfgvxOptions,
  base: AdfgvxResult
): AdfgvxResult {
  const upper = text.toUpperCase();
  let cipher = "";
  let invalid = 0;
  for (const char of upper) {
    if (base.labels.includes(char)) {
      cipher += char;
    } else if (LETTER_OR_DIGIT.test(char)) {
      invalid += 1;
    }
  }
  const moved = columnarDecode(cipher, {
    key: options.transposition,
    secondKey: null,
    filler: "",
  });
  const fractionated = moved.output;
  const unpaired = fractionated.length % 2 === 1 ? fractionated.slice(-1) : "";
  const usable = unpaired === "" ? fractionated : fractionated.slice(0, -1);
  const pairs: AdfgvxPair[] = [];
  for (let index = 0; index < usable.length; index += 2) {
    const pair = usable.slice(index, index + 2);
    const row = base.labels.indexOf(pair.charAt(0));
    const column = base.labels.indexOf(pair.charAt(1));
    pairs.push({ plain: base.square.charAt(row * base.size + column), pair });
  }
  return {
    ...base,
    pairs,
    fractionated,
    grid: moved.grids[0] ?? null,
    output: pairs.map((item) => item.plain).join(""),
    invalid,
    unpaired,
  };
}

export function adfgvxTransform(
  text: string,
  options: AdfgvxOptions,
  direction: CipherDirection
): AdfgvxResult {
  const size = gridSize(options.kind);
  const square = buildSquare(options.keyword, options.kind, options.fill);
  const base = emptyResult(square, size, adfgvxLabels(options.kind));
  if (!transpositionUsable(options.transposition)) {
    return base;
  }
  return direction === "encode"
    ? encode(text, options, base)
    : decode(text, options, base);
}
